import { useEffect, useMemo, useRef, useState } from 'react';

import { useGetPredictionTechsQuery } from '@/app/services/equipment';
import { EquipmentDropdown } from '@/components/common/equipment-dropdown/EquipmentDropdown';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import type { FreshStrawQCForm } from '../../types';
import type {
  FreshStrawDiscardSharedInputs,
  FreshStrawFreezeSharedInputs,
  FreshStrawImplantSharedInputs,
  FreshStrawRow,
} from './payload';
import { useFreshStrawActions } from './useFreshStrawActions';
import { toWallClockDate } from '@/utils/wallClock';

/**
 * FreshStrawActionPanel — the save surface for the fresh-straw module.
 *
 * Holds local form state for everything except the session-derived fields
 * (`transferDate`, `transferSite`), which come from `useFreshStrawSession` via
 * the parent page. Selected rows + their per-row stage/grade entries come from
 * the embryo panel via the `rows` prop.
 *
 * Not yet wired:
 *   - Shared vet/tech/farm-location dropdowns — Phase 2 polish. Text input for
 *     the raw id is good enough for the first end-to-end pass.
 *   - Mobile FAB — separate component, wired at the page level.
 */

export interface FreshStrawActionPanelProps {
  /** Selected fertilization rows with their per-row stage/grade entries. */
  rows: FreshStrawRow[];
  /** URL-synced session — read-only from the panel's perspective. */
  session: {
    transferDate: string;
    transferSite: string;
  };
  /** Whether the row math invariant passes (from selectFertilizationRowMath). */
  rowMathOk: boolean;
  /** Called on successful save so the parent can refetch + clear selection. */
  onSaved?: (
    action: 'implant' | 'freeze' | 'discard',
    rowsSaved: number,
  ) => void;
  className?: string;
}

interface PanelFormState {
  // Shared across actions — grader display name is derived from graderId
  // via the prediction-techs cache, not tracked here.
  graderId: string;
  loadById: string;
  loadDate: string;
  // QC
  qc: Partial<FreshStrawQCForm>;
  // Implant-only
  recip: string;
  farmLocationId: string;
  autoAssignStraws: boolean;
  calfType: string;
  // Freeze-only
  freezeDate: Date | undefined;
  freezerId: string;
}

const emptyForm = (): PanelFormState => ({
  graderId: '',
  loadById: '',
  loadDate: '',
  qc: {},
  recip: '',
  farmLocationId: '',
  autoAssignStraws: false,
  calfType: '',
  freezeDate: undefined,
  freezerId: '',
});

export function FreshStrawActionPanel({
  rows,
  session,
  rowMathOk,
  onSaved,
  className,
}: FreshStrawActionPanelProps) {
  const [form, setForm] = useState<PanelFormState>(emptyForm);
  const actionsRowRef = useRef<HTMLDivElement>(null);
  const [showFab, setShowFab] = useState(false);

  // Prediction techs feed the grader / loaded-by dropdowns. We also use the
  // cached list to derive display names for the selected ids so the proc
  // can receive both `@grader7` (name) and `@grader7id` (Guid).
  const { data: techs } = useGetPredictionTechsQuery({
    where: {
      or: [{ farmlocation: { eq: false } }, { farmlocation: { eq: null } }],
    },
  });
  const techNameById = useMemo(() => {
    const map = new Map<string, string>();
    techs?.forEach((tech: { id?: string | null; name?: string | null }) => {
      if (tech?.id && tech?.name) map.set(tech.id, tech.name);
    });
    return map;
  }, [techs]);
  const graderName = form.graderId
    ? (techNameById.get(form.graderId) ?? null)
    : null;

  // Mobile FAB reveal: IntersectionObserver watches the in-panel action row.
  // When the user has scrolled past it, surface the FAB so saves are always
  // reachable without scrolling. Matches the fert-work pattern
  // (see `CLAUDE.md` "FAB pattern" and `pages/fert-work/.../GenerateRuns/index.tsx:293`).
  useEffect(() => {
    const el = actionsRowRef.current;
    if (!el) {
      setShowFab(false);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setShowFab(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const sharedBase = useMemo(
    () => ({
      transferDate: session.transferDate || undefined,
      transferSite: session.transferSite || undefined,
      grader: graderName,
      graderId: form.graderId || null,
      loadById: form.loadById || null,
      loadDate: form.loadDate || null,
      qc: form.qc,
    }),
    [session.transferDate, session.transferSite, form, graderName],
  );

  const implantShared: FreshStrawImplantSharedInputs = useMemo(
    () => ({
      ...sharedBase,
      recip: form.recip,
      farmLocationId: form.farmLocationId || null,
      autoAssignStraws: form.autoAssignStraws,
      calfType: form.calfType ? Number(form.calfType) : null,
    }),
    [
      sharedBase,
      form.recip,
      form.farmLocationId,
      form.autoAssignStraws,
      form.calfType,
    ],
  );

  const freezeShared: FreshStrawFreezeSharedInputs = useMemo(
    () => ({
      ...sharedBase,
      freezeDate: form.freezeDate
        ? toWallClockDate(form.freezeDate)
        : undefined,
      freezerId: form.freezerId || null,
    }),
    [sharedBase, form.freezeDate, form.freezerId],
  );

  const discardShared: FreshStrawDiscardSharedInputs = sharedBase;

  const { handleImplant, handleFreeze, handleDiscard, isSaving } =
    useFreshStrawActions({
      rows,
      implantShared,
      freezeShared,
      discardShared,
      onSaved: (action, rowsSaved) => {
        onSaved?.(action, rowsSaved);
        // Reset the form on success — keeps users from accidentally
        // double-saving with stale QC.
        setForm(emptyForm());
      },
    });

  const disabled = isSaving || rows.length === 0 || !rowMathOk;

  const updateForm = <K extends keyof PanelFormState>(
    key: K,
    value: PanelFormState[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const updateQc = <K extends keyof FreshStrawQCForm>(
    key: K,
    value: FreshStrawQCForm[K],
  ) => setForm((prev) => ({ ...prev, qc: { ...prev.qc, [key]: value } }));

  return (
    <aside
      className={cn(
        'ring-border bg-background flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg ring-1',
        className,
      )}
      data-slot="fresh-straw-action-panel"
    >
      <div className="bg-muted/30 flex items-center justify-between border-b px-3 py-1.5">
        <h2 className="text-xs font-semibold tracking-wide uppercase">
          Save action
        </h2>
        <span className="text-muted-foreground text-xs">
          {rows.length} selected
        </span>
      </div>

      <div
        ref={actionsRowRef}
        className="flex gap-1.5 border-b px-3 py-2"
        data-slot="fresh-straw-action-row"
      >
        <Button
          type="button"
          size="xs"
          onClick={handleImplant}
          disabled={disabled}
          data-testid="action-implant"
          className="flex-1"
        >
          Implant
        </Button>
        <Button
          type="button"
          size="xs"
          variant="secondary"
          onClick={handleFreeze}
          disabled={disabled}
          data-testid="action-freeze"
          className="flex-1"
        >
          Freeze
        </Button>
        <Button
          type="button"
          size="xs"
          variant="destructive"
          onClick={handleDiscard}
          disabled={disabled}
          data-testid="action-discard"
          className="flex-1"
        >
          Discard
        </Button>
      </div>

      {!rowMathOk && rows.length > 0 && (
        <p className="text-destructive border-b px-3 py-2 text-xs">
          Row math is off — verify client transfer / frozen / discarded /
          shipped counts before saving.
        </p>
      )}

      <div
        className="flex min-h-0 flex-1 flex-col overflow-y-auto"
        aria-disabled={isSaving}
      >
        <section>
          <div className="bg-muted/30 border-b px-3 py-1.5">
            <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              Shared
            </h3>
          </div>
          <div className="flex flex-col gap-2 px-3 py-2">
            <Field>
              <FieldLabel htmlFor="fs-grader">Grader</FieldLabel>
              <EquipmentDropdown
                inputId="fs-grader"
                value={form.graderId || undefined}
                onChange={(e) => updateForm('graderId', e.value ?? '')}
                placeholder="Select grader"
                showClear
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="fs-load-by">Loaded by</FieldLabel>
              <EquipmentDropdown
                inputId="fs-load-by"
                value={form.loadById || undefined}
                onChange={(e) => updateForm('loadById', e.value ?? '')}
                placeholder="Select tech"
                showClear
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="fs-load-date">Load date</FieldLabel>
              <Input
                id="fs-load-date"
                value={form.loadDate}
                onChange={(e) => updateForm('loadDate', e.target.value)}
                placeholder="AM / PM"
                disabled={isSaving}
              />
            </Field>
          </div>
        </section>

        <section>
          <div className="bg-muted/30 border-y px-3 py-1.5">
            <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              QC
            </h3>
          </div>
          <div className="flex flex-col gap-2 px-3 py-2">
            <Field>
              <FieldLabel htmlFor="fs-ehm">EHM date</FieldLabel>
              <DatePicker
                value={
                  form.qc.ehmDate instanceof Date ? form.qc.ehmDate : undefined
                }
                onChange={(date) => updateQc('ehmDate', date as Date)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="fs-straw-lot">Straw lot #</FieldLabel>
              <Input
                id="fs-straw-lot"
                value={form.qc.strawLotNumber ?? ''}
                onChange={(e) => updateQc('strawLotNumber', e.target.value)}
                disabled={isSaving}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="fs-straw-inc">Straw incubator #</FieldLabel>
              <Input
                id="fs-straw-inc"
                value={form.qc.strawIncubatorNumber ?? ''}
                onChange={(e) =>
                  updateQc('strawIncubatorNumber', e.target.value)
                }
                disabled={isSaving}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="fs-gun-warmer">Gun warmer temp</FieldLabel>
              <Input
                id="fs-gun-warmer"
                value={form.qc.gunWarmerTemp ?? ''}
                onChange={(e) => updateQc('gunWarmerTemp', e.target.value)}
                disabled={isSaving}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="fs-thaw">Thaw thermos temp</FieldLabel>
              <Input
                id="fs-thaw"
                value={form.qc.thawThermosTemp ?? ''}
                onChange={(e) => updateQc('thawThermosTemp', e.target.value)}
                disabled={isSaving}
              />
            </Field>
          </div>
        </section>

        <section>
          <div className="bg-muted/30 border-y px-3 py-1.5">
            <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              Implant
            </h3>
          </div>
          <div className="flex flex-col gap-2 px-3 py-2">
            <Field>
              <FieldLabel htmlFor="fs-recip">Recip</FieldLabel>
              <Input
                id="fs-recip"
                value={form.recip}
                onChange={(e) => updateForm('recip', e.target.value)}
                disabled={isSaving}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="fs-farm">Farm location ID</FieldLabel>
              <Input
                id="fs-farm"
                value={form.farmLocationId}
                onChange={(e) => updateForm('farmLocationId', e.target.value)}
                disabled={isSaving}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="fs-calf-type">Calf type</FieldLabel>
              <Input
                id="fs-calf-type"
                type="number"
                value={form.calfType}
                onChange={(e) => updateForm('calfType', e.target.value)}
                disabled={isSaving}
              />
            </Field>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={form.autoAssignStraws}
                onChange={(e) =>
                  updateForm('autoAssignStraws', e.target.checked)
                }
                disabled={isSaving}
              />
              Auto-assign straws
            </label>
          </div>
        </section>

        <section>
          <div className="bg-muted/30 border-y px-3 py-1.5">
            <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              Freeze
            </h3>
          </div>
          <div className="flex flex-col gap-2 px-3 py-2">
            <Field>
              <FieldLabel htmlFor="fs-freeze-date">Freeze date</FieldLabel>
              <DatePicker
                value={form.freezeDate}
                onChange={(date) => updateForm('freezeDate', date)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="fs-freezer">
                Freezer ID (optional)
              </FieldLabel>
              <Input
                id="fs-freezer"
                value={form.freezerId}
                onChange={(e) => updateForm('freezerId', e.target.value)}
                placeholder="Defaults to Client equipment"
                disabled={isSaving}
              />
            </Field>
          </div>
        </section>
      </div>

      {showFab && (
        <div
          data-slot="fresh-straw-fab"
          className="fixed right-4 bottom-4 z-50 flex flex-col-reverse items-end gap-2 sm:right-6 sm:bottom-6 sm:gap-3 lg:hidden"
          role="group"
          aria-label="Save actions"
        >
          <Button
            type="button"
            onClick={handleImplant}
            disabled={disabled}
            className="!h-12 !w-12 rounded-full shadow-lg sm:!h-14 sm:!w-14"
            aria-label="Implant"
            data-testid="fab-implant"
          >
            <span className="sr-only">Implant</span>
            <span aria-hidden>I</span>
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleFreeze}
            disabled={disabled}
            className="!h-9 !w-9 rounded-full shadow-lg sm:!h-10 sm:!w-10"
            aria-label="Freeze"
            data-testid="fab-freeze"
          >
            <span className="sr-only">Freeze</span>
            <span aria-hidden>F</span>
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDiscard}
            disabled={disabled}
            className="!h-9 !w-9 rounded-full shadow-lg sm:!h-10 sm:!w-10"
            aria-label="Discard"
            data-testid="fab-discard"
          >
            <span className="sr-only">Discard</span>
            <span aria-hidden>D</span>
          </Button>
        </div>
      )}
    </aside>
  );
}
